import React, { useState, useEffect, useMemo } from "react"
import { Switch } from "antd"
import { useParams, useNavigate } from "react-router-dom"
import {
  Card,
  CardBody,
  Table,
  Button,
  Row,
  Col,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import {
  FaArrowLeft,
  FaArrowUp,
  FaArrowDown,
  FaFilePdf,
  FaFileImage,
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaEnvelope,
  FaEye,
  FaDownload,
  FaWhatsapp,
  FaIdCard,
  FaUpload,
  FaEdit,
} from "react-icons/fa"
import Configs from "../../config"
import { fetchData, FetchDeleteData, FetchPostData, FetchPutData } from "utils/Common_api_functions"
import { FaDeleteLeft } from "react-icons/fa6"
import Swal from "sweetalert2"
import UploadDocumentModal from "./UploadDocumentModal"
import CustomerModal from "./CustomerModal"

const PAGE_SIZE = 10

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "jfif"]

const isImageFile = fileName => {
  if (!fileName) return false
  const ext = fileName.split(".").pop().toLowerCase()
  return IMAGE_EXTENSIONS.includes(ext)
}

const CustomerView = () => {
  document.title = "View Customer | Siddhi Tax Consultant"

  const { id } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [customer, setCustomer] = useState(null)
  const [docs, setDocs] = useState([])
  const [loadingDocs, setLoadingDocs] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [filesSortOrder, setFilesSortOrder] = useState("asc")
  const [uploadModal, setUploadModal] = useState(false)

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editCustomerData, setEditCustomerData] = useState({})

  const [categories, setCategories] = useState([])
  // const [allDocTypes, setAllDocTypes] = useState([])
  const [loadingTypes, setLoadingTypes] = useState(false)

  const [filterPerson, setFilterPerson] = useState("")
  const [filterCategory, setFilterCategory] = useState("")
  const [filterDocType, setFilterDocType] = useState("")
  const [filterLoading, setFilterLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    if (id) {
      getCustomer()
      getCustomerDocs()
      getCategories()
      // getDocumentTypes()
    }
  }, [id])

  useEffect(() => {
    if (customer?.id) setFilterPerson("customer")
  }, [customer])

  useEffect(() => {
    setCurrentPage(1)
  }, [filterPerson, filterCategory, filterDocType])

  useEffect(() => {
    setFilterLoading(true)
    const timer = setTimeout(() => setFilterLoading(false), 500)
    return () => clearTimeout(timer)
  }, [filterPerson, filterCategory, filterDocType])

  async function getCustomer() {
    const APIdata = await fetchData(setLoading, `customer/${id}`)
    if (APIdata?.success) setCustomer(APIdata?.data)
  }

  // async function getCustomerDocs() {
  //   const APIdata = await fetchData(setLoadingDocs, `customer-documents/${id}`)
  //   if (APIdata?.success) setDocs(APIdata?.data || [])
  // }

  async function getCustomerDocs() {
    const APIdata = await fetchData(
      setLoadingDocs,
      `customer-documents/${id}`
    )

    if (APIdata?.success) {
      setDocs(
        Array.isArray(APIdata.data)
          ? APIdata.data
          : APIdata.data
            ? [APIdata.data]
            : []
      )
    }
  }

  async function getCategories() {
    const APIdata = await fetchData(setLoadingTypes, "document-category")
    if (APIdata?.success) setCategories(APIdata?.data || [])
  }

  async function toggleShare(doc, checked) {
    Swal.fire({
      title: `Are you sure you want to ${checked ? "Share" : "Unshare"} this document with the customer?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const res = await FetchPutData(
          () => {},
          "customer-documents",
          doc.id,
          { toBeShared: checked }
        )
        if (res?.data?.success) {
          setDocs(prev =>
            prev.map(d => (d.id === doc.id ? { ...d, toBeShared: checked } : d))
          )
          Swal.fire({
            title: `Status for Document changed to  ${checked ? "Shared" : "Unshared"} Successfully.`,
            icon: "success",
          })
        } else {
          Swal.fire({
            title: "Error Updating Document.",
            text: res?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  // async function getDocumentTypes() {
  //   const APIdata = await fetchData(setLoadingTypes, "document-types")
  //   if (APIdata?.success) setAllDocTypes(APIdata?.data || [])
  // }

  const docTypeOptions = useMemo(() => {
    if (!filterCategory) {
      return categories.flatMap(cat => cat.documentTypes || [])
    }

    const selectedCategory = categories.find(
      cat => String(cat.id) === String(filterCategory)
    )

    return selectedCategory?.documentTypes || []
  }, [filterCategory, categories])

  // Reset doc type filter when category changes
  useEffect(() => {
    setFilterDocType("")
  }, [filterCategory])

  const formatDateTime = isoString => {
    if (!isoString) return "—"
    const d = new Date(isoString)
    const dd = String(d.getDate()).padStart(2, "0")
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const yyyy = d.getFullYear()
    const hh = String(d.getHours()).padStart(2, "0")
    const min = String(d.getMinutes()).padStart(2, "0")
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`
  }

  const personOptions = useMemo(() => {
    if (!customer) return []
    return [
      { label: customer.customerName, value: "customer" },
      ...(customer.members || []).map(m => ({
        label: m.name,
        value: `member_${m.id}`,
      })),
    ]
  }, [customer])

  const filteredDocs = useMemo(() => {
    return docs.filter(doc => {
      let personMatch = true
      if (filterPerson) {
        if (filterPerson === "customer") {
          personMatch = !doc.member
        } else {
          const memberId = filterPerson.replace("member_", "")
          personMatch = !!doc.member && String(doc.member.id) === memberId
        }
      }

      const categoryMatch =
        !filterCategory ||
        String(doc.documentType?.documentCategory?.id) === String(filterCategory)

      const typeMatch =
        !filterDocType ||
        String(doc.documentType?.id) === String(filterDocType)

      return personMatch && categoryMatch && typeMatch
    }).sort((a, b) => {
      const yearA = a.financialYear || ""
      const yearB = b.financialYear || ""
      if (yearA === yearB) return 0
      if (!yearA) return 1
      if (!yearB) return -1
      return yearB.localeCompare(yearA)
    })
  }, [docs, filterPerson, filterCategory, filterDocType])

  const totalPages = Math.ceil(filteredDocs.length / PAGE_SIZE)
  const paginatedDocs = filteredDocs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  const openViewModal = doc => {
    setSelectedDoc(doc)
    setSelectedFiles([])
    setFilesSortOrder("asc")
    setViewModal(true)
  }

  const toggleFileSelection = fileId => {
    setSelectedFiles(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    )
  }

  const allFileIds = selectedDoc?.files?.map(f => f.id) || []
  const allSelected = allFileIds.length > 0 && selectedFiles.length === allFileIds.length

  const handleSelectAll = () => {
    setSelectedFiles(allSelected ? [] : allFileIds)
  }

  const handleDeleteSelected = async () => {
    const filesToDelete = selectedDoc.files.filter(f => selectedFiles.includes(f.id))
    Swal.fire({
      title: `Delete ${filesToDelete.length} file(s)?`,
      text: "This will permanently delete all selected files.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete them!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        let successCount = 0
        let errorCount = 0
        for (const file of filesToDelete) {
          const APIdata = await FetchDeleteData(setLoading, "customer-documents", file.id)
          if (APIdata?.data?.success) successCount++
          else errorCount++
        }
        if (errorCount === 0) {
          Swal.fire({ title: "Deleted Successfully.", text: `${successCount} file(s) deleted.`, icon: "success" })
        } else {
          Swal.fire({ title: "Partial Deletion.", text: `${successCount} deleted, ${errorCount} failed.`, icon: "warning" })
        }
        setSelectedFiles([])
        setViewModal(false)
        getCustomerDocs()
      }
    })
  }

  const handleViewFile = file => {
    window.open(file.driveFileUrl, "_blank")
  }

  const handleDownloadFile = file => {
    window.open(
      `https://drive.google.com/uc?export=download&id=${file.driveFileId}`,
      "_blank"
    )
  }

  const handleDownloadSelected = () => {
    const filesToDownload = selectedDoc.files.filter(f => selectedFiles.includes(f.id))
    filesToDownload.forEach((file, index) => {
      setTimeout(() => {
        const a = document.createElement("a")
        a.href = `https://drive.google.com/uc?export=download&id=${file.driveFileId}`
        a.target = "_blank"
        a.rel = "noopener noreferrer"
        a.click()
      }, index * 500)
    })
  }

  const handleUpload = async docData => {
    const APIdata = await FetchPostData(setLoading, "customer-documents", docData)
    if (APIdata?.data?.success) {
      Swal.fire({ title: "Documents Uploaded Successfully.", icon: "success" })
    } else {
      Swal.fire({ title: "Error Uploading Documents.", icon: "error" })
    }
    getCustomerDocs()
  }

  const openEditModal = async () => {
    setEditCustomerData({})
    setEditModalOpen(true)
    const APIdata = await fetchData(setLoading, `customer/${id}`)
    setEditCustomerData(APIdata?.success ? APIdata.data : { ...customer })
  }

  const handleEditSubmit = async payload => {
    const APIdata = await FetchPutData(setLoading, "customer", id, payload)
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Customer Updated Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
      setEditModalOpen(false)
      getCustomer()
    } else {
      Swal.fire({
        title: "Error Updating Customer.",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  if (loading && !customer) {
    return (
      <div className="page-content">
        <Loader />
      </div>
    )
  }

  if (!customer) {
    return (
      <div className="page-content">
        <Breadcrumb title="Customers" breadcrumbItem="View Customer" />
        <Card>
          <CardBody className="text-center py-5 text-muted">
            Customer not found.
          </CardBody>
        </Card>
      </div>
    )
  }

  const handleDelete = async (file) => {
    Swal.fire({
      title: `Are you sure you want to delete "${file?.originalName}"?`,
      text: "This will delete the file permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "customer-documents", file?.id)
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "File Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          // navigate(-1);
          setViewModal(false);
          getCustomerDocs()
        } else {
          Swal.fire({
            title: "Error Deleting File.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Customers" breadcrumbItem="View Customer" />

      <div className="d-flex justify-content-end mb-3">
        <Button
          className="btn-blue rounded-4 d-flex align-items-center gap-2"
          onClick={() => navigate(`${Configs.appurl}customers`)}
        >
          <FaArrowLeft size={13} /> Back to Customers
        </Button>
      </div>

      {/* Customer Info Card */}
      <Card className="mb-4 shadow-lg">
        <CardBody>
          <div className="d-flex align-items-center gap-3 mb-4">
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #172B4B, #6f42c1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 800,
                fontSize: 24,
                flexShrink: 0,
              }}
            >
              {customer.customerName?.[0]?.toUpperCase()}
            </div>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2">
                <h4 className="fw-bold mb-0" style={{ color: "#172B4B" }}>
                  {customer.customerName}
                </h4>
                <Button
                  size="sm"
                  className="text-white border-0 shadow-none d-inline-flex align-items-center gap-1"
                  style={{ backgroundColor: "#172B4B", marginLeft: '20px' }}
                  onClick={openEditModal}
                >
                  <FaEdit size={13} /> Edit Customer
                </Button>
              </div>
              <div style={{ fontSize: 13, color: "#888" }}>
                Customer ID: #{customer.id}
              </div>
            </div>
          </div>

          <Row className="g-3 mb-3">
            <Col md={4}>
              <div className="d-flex align-items-start gap-2">
                <FaEnvelope size={14} color="#172B4B" style={{ marginTop: 3 }} />
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#999",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                    }}
                  >
                    Email
                  </div>
                  <div className="fw-semibold">{customer.email || "—"}</div>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-2">
                <FaPhone size={14} color="#172B4B" style={{ marginTop: 3 }} />
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#999",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                    }}
                  >
                    Phone
                  </div>
                  <div className="fw-semibold">{customer.phone}</div>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-2">
                <FaWhatsapp size={14} color="#172B4B" style={{ marginTop: 3 }} />
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#999",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                    }}
                  >
                    WhatsApp
                  </div>
                  <div className="fw-semibold">
                    {customer.alternatePhone || "—"}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-2">
                <FaIdCard size={14} color="#172B4B" style={{ marginTop: 3 }} />
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#999",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                    }}
                  >
                    Aadhar Card
                  </div>
                  <div className="fw-semibold">
                    {customer.aadharCard || "—"}
                  </div>
                </div>
              </div>
            </Col>

            <Col md={4}>
              <div className="d-flex align-items-start gap-2">
                <FaIdCard size={14} color="#172B4B" style={{ marginTop: 3 }} />
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#999",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                    }}
                  >
                    PAN Card
                  </div>
                  <div className="fw-semibold">
                    {customer.panCard || "—"}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-2">
                <FaMapMarkerAlt
                  size={14}
                  color="#172B4B"
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#999",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                    }}
                  >
                    Address
                  </div>
                  <div className="fw-semibold">{customer.address || "—"}</div>
                </div>
              </div>
            </Col>
          </Row>

          {customer.members?.length > 0 && (
            <div
              style={{
                borderTop: "1px solid rgba(0,0,0,0.07)",
                paddingTop: 16,
              }}
            >
              <div
                className="d-flex align-items-center gap-2 mb-2"
                style={{ fontSize: 13, fontWeight: 600, color: "#172B4B" }}
              >
                <FaUser size={13} /> Members
              </div>
              <div className="d-flex flex-wrap gap-2">
                {customer.members.map((m, i) => (
                  <span
                    key={i}
                    className="badge rounded-1 fs-6 bg-blue text-white px-3 py-2"
                  >
                    {m.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Files View Modal */}
      <Modal
        isOpen={viewModal}
        toggle={() => { setViewModal(false); setSelectedFiles([]) }}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => { setViewModal(false); setSelectedFiles([]) }}>
          <div className="d-flex align-items-center justify-content-between gap-3" style={{ width: "100%" }}>
            <span className="d-flex align-items-center gap-2">
              <FaFilePdf color="#dc3545" size={15} />
              {selectedDoc?.documentType?.documentType} — Documents
            </span>
            <Button
              size="sm"
              className="bg-blue text-white border-1 border-white p-2 px-3 shadow-none d-inline-flex align-items-center gap-1"
              onClick={() => setUploadModal(true)}
            >
              <FaUpload size={13} /> Upload
            </Button>
          </div>
        </ModalHeader>
        <ModalBody>
          {selectedDoc?.files?.length > 0 ? (
            <>
              {selectedFiles.length > 0 && (
                <div className="d-flex justify-content-end gap-2 mb-2">
                  <Button
                    size="sm"
                    color="success"
                    className="border-0 shadow-none d-inline-flex align-items-center gap-1"
                    onClick={handleDownloadSelected}
                  >
                    <FaDownload size={13} /> Download Selected ({selectedFiles.length})
                  </Button>
                  <Button
                    size="sm"
                    color="danger"
                    className="border-0 shadow-none d-inline-flex align-items-center gap-1"
                    onClick={handleDeleteSelected}
                  >
                    <FaDeleteLeft size={13} /> Delete Selected ({selectedFiles.length})
                  </Button>
                </div>
              )}
            <Table bordered responsive>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: 40 }}>
                    <Input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                      style={{ cursor: "pointer" }}
                    />
                  </th>
                  <th>#</th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                    onClick={() => setFilesSortOrder(o => o === "asc" ? "desc" : "asc")}
                  >
                    File Name{" "}
                    {filesSortOrder === "asc"
                      ? <FaArrowUp size={11} />
                      : <FaArrowDown size={11} />
                    }
                  </th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(filesSortOrder === "asc" ? selectedDoc.files : [...selectedDoc.files].reverse()).map((file, idx) => {
                  const isImage = isImageFile(file.originalName)
                  return (
                    <tr key={file.id}>
                      <td>
                        <Input
                          type="checkbox"
                          checked={selectedFiles.includes(file.id)}
                          onChange={() => toggleFileSelection(file.id)}
                          style={{ cursor: "pointer" }}
                        />
                      </td>
                      <td>{idx + 1}</td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <span className="d-flex align-items-center gap-2">
                            {isImage
                              ? <FaFileImage color="#556ee6" size={13} />
                              : <FaFilePdf color="#dc3545" size={13} />
                            }
                            {file.originalName || file.driveFileId}
                          </span>
                          {/* {isImage && file.driveFileUrl && (
                            <img
                              src={file.driveFileUrl}
                              alt={file.originalName}
                              style={{
                                maxHeight: 80,
                                maxWidth: 120,
                                objectFit: "cover",
                                borderRadius: 4,
                                border: "1px solid #dee2e6",
                                cursor: "pointer",
                              }}
                              onClick={() => handleViewFile(file)}
                            />
                          )} */}
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-inline-flex gap-2">
                          <Button
                            size="sm"
                            className="bg-blue text-white border-0 shadow-none d-inline-flex align-items-center gap-1"
                            onClick={() => handleViewFile(file)}
                          >
                            <FaEye size={13} /> View
                          </Button>
                          <Button
                            size="sm"
                            color="success"
                            className="border-0 shadow-none d-inline-flex align-items-center gap-1"
                            onClick={() => handleDownloadFile(file)}
                          >
                            <FaDownload size={13} /> Download
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            className="border-0 shadow-none d-inline-flex align-items-center gap-1"
                            onClick={() => handleDelete(file)}
                          >
                            <FaDeleteLeft size={13} /> Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            </>
          ) : (
            <div className="text-center py-3 text-muted">No files available.</div>
          )}
        </ModalBody>
      </Modal>

      <UploadDocumentModal
        open={uploadModal}
        toggle={() => setUploadModal(false)}
        customer={customer}
        onUpload={handleUpload}
        prefill={viewModal && selectedDoc ? {
          uploadFor: selectedDoc.member ? selectedDoc.member.id : customer?.id,
          categoryId: selectedDoc.documentType?.documentCategory?.id,
          documentId: selectedDoc.documentType?.id,
          year: selectedDoc.financialYear,
        } : null}
      />

      <CustomerModal
        open={editModalOpen}
        toggle={() => setEditModalOpen(false)}
        selectedCustomer={editCustomerData}
        setSelectedCustomer={setEditCustomerData}
        viewOnly={false}
        onSubmit={handleEditSubmit}
      />

      {/* Documents Section */}
      <Card className="shadow-lg">
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="fw-bold mb-0" style={{ color: "#172B4B" }}>
              <FaFilePdf className="me-2" />
              Documents
            </h5>
            <Button
              className="bg-blue text-white border-0 shadow-none d-inline-flex align-items-center gap-1"
              onClick={() => setUploadModal(true)}
            >
              <FaUpload size={13} /> Upload Document
            </Button>
          </div>

          {/* Filters */}
          <Row className="mb-3 g-2">
            <Col md={3}>
              <Label className="fw-semibold mb-1">Customer / Member</Label>
              <Input
                type="select"
                value={filterPerson}
                onChange={e => {
                  setFilterPerson(e.target.value)
                  setFilterCategory("")
                  setFilterDocType("")
                }}
                className="rounded-4"
              >
                {personOptions.map((opt, i) => (
                  <option key={i} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label className="fw-semibold mb-1">Category</Label>
              <Input
                type="select"
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="rounded-4"
                disabled={loadingTypes}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.documentCategory}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label className="fw-semibold mb-1">Document Type</Label>
              <Input
                type="select"
                value={filterDocType}
                onChange={e => setFilterDocType(e.target.value)}
                className="rounded-4"
                disabled={loadingTypes}
              >
                <option value="">All Types</option>
                {docTypeOptions.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.documentType}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          {loadingDocs || filterLoading ? (
            <div className="text-center py-4 text-muted">
              Loading documents...
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No documents found.
            </div>
          ) : (
            <>
              <Table bordered responsive>
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Category</th>
                    <th>Document Type</th>
                    <th>Financial Year</th>
                    <th>Uploaded By</th>
                    <th className="text-center">Total Docs</th>
                    <th>Created At</th>
                    <th className="text-center">Share with Customer</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocs.map((doc, i) => (
                    doc.files?.length != 0 &&
                    <tr key={doc.id}>
                      <td>{(currentPage - 1) * PAGE_SIZE + i + 1}</td>
                      <td>{doc.documentType?.documentCategory?.documentCategory || "—"}</td>
                      <td>
                        <span className="d-flex align-items-center gap-2">
                          <FaFilePdf color="#dc3545" size={14} />
                          {doc.documentType?.documentType}
                        </span>
                      </td>
                      <td>{doc.financialYear || "—"}</td>
                      <td>{doc?.addedBy?.name}</td>
                      <td className="text-center">
                        <span className="badge bg-secondary fs-6">
                          {doc.files?.length || 0}
                        </span>
                      </td>
                      <td>{formatDateTime(doc.createdAt)}</td>
                      <td className="text-center">
                        <Switch
                          checked={!!doc.toBeShared}
                          onChange={checked => toggleShare(doc, checked)}
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          className="bg-blue text-white border-0 shadow-none d-inline-flex align-items-center gap-1"
                          onClick={() => openViewModal(doc)}
                        >
                          <FaEye size={13} /> View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <nav className="d-flex justify-content-between align-items-center mt-3">
                <div style={{ fontSize: 13, color: "#888" }}>
                  Showing {(currentPage - 1) * PAGE_SIZE + 1} to{" "}
                  {Math.min(currentPage * PAGE_SIZE, filteredDocs.length)} of{" "}
                  {filteredDocs.length} entries
                </div>
                <ul className="pagination mb-0">
                  <li
                    className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => {
                    const pageNum = i + 1
                    return (
                      <li
                        key={pageNum}
                        className={`page-item ${pageNum === currentPage ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    )
                  })}
                  <li
                    className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        setCurrentPage(p => Math.min(p + 1, totalPages))
                      }
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default CustomerView
